import { RADIO_INPUT_TYPE, SELECT_INPUT_TYPE, Yup } from "@mavichpe/dynamic-forms";
import {
    OTHER_CHARGE_AMOUNT_MODES,
    OTHER_CHARGE_THIRD_PARTY_ID_TYPES,
    OTHER_CHARGE_TYPES,
    OTHER_CHARGE_TYPE_OTHERS,
    OTHER_CHARGE_TYPE_THIRD_PARTY,
} from "@utils/Constants";
import {
    OTHER_CHARGE_DETALLE_MAX_LENGTH,
    OTHER_CHARGE_IDENTIFICACION_MAX_LENGTH,
    OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH,
    OTHER_CHARGE_OTROS_MAX_LENGTH,
    OTHER_CHARGE_OTROS_MIN_LENGTH,
    OTHER_CHARGE_PERCENT_MAX_FRACTION_DIGITS,
    THIRD_PARTY_NAME_LENGTH_MESSAGE,
    isValidThirdPartyName,
} from "@utils/DocumentOtherCharges";
import { validateIdentification } from "@utils/Identification";
import { withPadronRegistration } from "@utils/Forms";
import { Button } from "reactstrap";

/**
 * Los limites salen de `DocumentOtherCharges.ts`, que los deriva del XSD v4.4: aqui no se
 * repite ningun numero magico, para que el formulario no pueda quedar mas permisivo que el
 * comprobante que Hacienda va a validar.
 */
const isThirdParty = (tipoDocumentoOC: unknown): boolean =>
    `${tipoDocumentoOC}` === OTHER_CHARGE_TYPE_THIRD_PARTY;

const isOthers = (tipoDocumentoOC: unknown): boolean =>
    `${tipoDocumentoOC}` === OTHER_CHARGE_TYPE_OTHERS;

const countFractionDigits = (value: number): number => {
    const [, fraction = ""] = `${value}`.split(".");
    return fraction.length;
};

export type DocumentOtherChargesFormHandlers = {
    /** Consulta el padron con lo que hay en el borrador y siembra el nombre del tercero. */
    onVerifyIdentification: () => void;
};

/**
 * Config parametrizada para poder inyectar el boton "Verificar", que necesita el `formRef`
 * del editor. Mismo patron que `buildAccountingPackageFormConfig`.
 */
const buildDocumentOtherChargesFormConfig = (handlers: DocumentOtherChargesFormHandlers) => ({
    submit: {
        label: "Agregar",
    },
    sections: [
        {
            hideBorderBottom: true,
            fields: [
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipoDocumentoOC",
                        "label": "Tipo de cargo",
                        "sizing": { "sm": 12 },
                        "options": OTHER_CHARGE_TYPES,
                        "validation": Yup.string().required("Seleccione un tipo de cargo válido"),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "tipoDocumentoOTROS",
                        "label": "Descripción del tipo de cargo",
                        "sizing": { "sm": 12 },
                        "maxLength": OTHER_CHARGE_OTROS_MAX_LENGTH,
                        "visibleIf": { "tipoDocumentoOC": OTHER_CHARGE_TYPE_OTHERS },
                        "validation": Yup.string().when("tipoDocumentoOC", {
                            is: isOthers,
                            then: (s) => s
                                .required(`Describa el tipo de cargo entre ${OTHER_CHARGE_OTROS_MIN_LENGTH} y ${OTHER_CHARGE_OTROS_MAX_LENGTH} caracteres`)
                                .min(OTHER_CHARGE_OTROS_MIN_LENGTH, `Describa el tipo de cargo entre ${OTHER_CHARGE_OTROS_MIN_LENGTH} y ${OTHER_CHARGE_OTROS_MAX_LENGTH} caracteres`)
                                .max(OTHER_CHARGE_OTROS_MAX_LENGTH, `Describa el tipo de cargo entre ${OTHER_CHARGE_OTROS_MIN_LENGTH} y ${OTHER_CHARGE_OTROS_MAX_LENGTH} caracteres`),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "identificacionTipo",
                        "label": "Tipo de identificación del tercero",
                        "sizing": { "sm": 12 },
                        "options": OTHER_CHARGE_THIRD_PARTY_ID_TYPES,
                        "visibleIf": { "tipoDocumentoOC": OTHER_CHARGE_TYPE_THIRD_PARTY },
                        "validation": Yup.string().when("tipoDocumentoOC", {
                            is: isThirdParty,
                            then: (s) => s.required("Seleccione el tipo de identificación del tercero"),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "identificacionNumero",
                        "label": "Identificación del tercero",
                        "sizing": { "sm": 12 },
                        "maxLength": OTHER_CHARGE_IDENTIFICACION_MAX_LENGTH,
                        "visibleIf": { "tipoDocumentoOC": OTHER_CHARGE_TYPE_THIRD_PARTY },
                        // Se reutiliza la misma validacion de cedula del bloque de cliente,
                        // que es por tipo de identificacion, no un largo fijo. Encima va la
                        // verificacion contra el padron: Hacienda rechaza con el codigo -105
                        // un cobro a nombre de un tercero que no esta inscrito.
                        "validation": Yup.string().when(["tipoDocumentoOC", "identificacionTipo"], {
                            is: (tipoDocumentoOC: unknown) => isThirdParty(tipoDocumentoOC),
                            then: (s) => withPadronRegistration(
                                s.test(
                                    "identificacion-tercero",
                                    "La identificación del tercero no es válida",
                                    function (value) {
                                        const { identificacionTipo } = this.parent as { identificacionTipo?: string };
                                        const result = validateIdentification(`${identificacionTipo || ""}`, `${value || ""}`);
                                        return result.valid || this.createError({ message: result.message });
                                    },
                                ),
                                "identificacionTipo",
                            ),
                            otherwise: (s) => s.notRequired(),
                        }),
                        "suffixButton": true,
                        "suffix": <Button
                            block
                            className="rounded-0"
                            color="primary"
                            type="button"
                            data-testid="other-charge-verify-identification"
                            onClick={handlers.onVerifyIdentification}>
                            Verificar
                        </Button>
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "nombreTercero",
                        "label": "Nombre del tercero",
                        "sizing": { "sm": 12 },
                        "maxLength": OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH,
                        "visibleIf": { "tipoDocumentoOC": OTHER_CHARGE_TYPE_THIRD_PARTY },
                        // El largo se mide con `isValidThirdPartyName`, sobre el nombre recortado:
                        // `.min()` dejaria pasar un "FABA " que Hacienda rechaza igual.
                        "validation": Yup.string().when("tipoDocumentoOC", {
                            is: isThirdParty,
                            then: (s) => s
                                .required(THIRD_PARTY_NAME_LENGTH_MESSAGE)
                                .test(
                                    "nombre-tercero-largo",
                                    THIRD_PARTY_NAME_LENGTH_MESSAGE,
                                    (value) => isValidThirdPartyName(value),
                                ),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "detalle",
                        "label": "Detalle",
                        "sizing": { "sm": 12 },
                        "maxLength": OTHER_CHARGE_DETALLE_MAX_LENGTH,
                        "validation": Yup.string()
                            .required("El detalle del cargo es requerido")
                            .max(OTHER_CHARGE_DETALLE_MAX_LENGTH, `El detalle no puede superar los ${OTHER_CHARGE_DETALLE_MAX_LENGTH} caracteres`),
                    },
                ],
                [
                    {
                        "type": RADIO_INPUT_TYPE,
                        "name": "mode",
                        "label": "Cómo se calcula",
                        "isHorizontalValues": true,
                        "sizing": { "sm": 12 },
                        "options": OTHER_CHARGE_AMOUNT_MODES,
                        "defaultValue": "amount",
                        "validation": Yup.string().required("Indique cómo se calcula el cargo"),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "porcentajeOC",
                        "label": "Porcentaje sobre la venta neta",
                        "suffix": "%",
                        "min": "0",
                        "sizing": { "sm": 12 },
                        "visibleIf": { "mode": "percent" },
                        "validation": Yup.number().when("mode", {
                            is: (mode: unknown) => `${mode}` === "percent",
                            then: (s) => s
                                .typeError("Indique un porcentaje mayor o igual a cero")
                                .required("Indique un porcentaje mayor o igual a cero")
                                .min(0, "Indique un porcentaje mayor o igual a cero")
                                .test(
                                    "decimales-porcentaje",
                                    `El porcentaje admite máximo ${OTHER_CHARGE_PERCENT_MAX_FRACTION_DIGITS} decimales`,
                                    (value) => value == null || countFractionDigits(value) <= OTHER_CHARGE_PERCENT_MAX_FRACTION_DIGITS,
                                ),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                    {
                        "type": "money",
                        "name": "montoCargo",
                        "label": "Monto del cargo",
                        "sizing": { "sm": 12 },
                        "visibleIf": { "mode": "amount" },
                        "validation": Yup.number().when("mode", {
                            is: (mode: unknown) => `${mode}` !== "percent",
                            then: (s) => s
                                .typeError("Indique un monto mayor o igual a cero")
                                .required("Indique un monto mayor o igual a cero")
                                .min(0, "Indique un monto mayor o igual a cero"),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
            ],
        },
    ],
});

export default buildDocumentOtherChargesFormConfig;
